import { CommunityTenancyData } from '../models/agreements/communityTenancy.data';
import { TenancyAgreementData } from '../models/agreements/tenancyAgr.data';
import { PersonLike, PhoneLike, ResidentLike, UserLike } from '../models/person/personLike.data';
import { LandlordType, ResidentData } from '../models/person/resident.data';
import { UserData } from '../models/users/user.data';
import { Nullish } from '../types/typeish.type';
import { ArrHlp } from './array.helper';
import { TimeHlp } from './time.helper';

export enum AgreementStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  FUTURE = 'future'
}

export interface UnitAccessVM {
  startDate: number;
  endDate: number | null;
  cancelDate: number | null;
  creationDate: number;
  creatorUserName: string;
}

/** */
export class UserHlp {
  /**
   * @param {TenancyAgreementData} agr
   * @return {boolean}
   */
  public static isCurrentAgreement(agr: TenancyAgreementData): boolean {
    return (
      agr.agreementStartDate < TimeHlp.todayAtEnd() &&
      ((agr.agreementEndDate ?? Number.MAX_VALUE) > TimeHlp.todayAt(0) || agr.infiniteTimePeriod)
    );
  }

  /**
   * @param {TenancyAgreementData} agr
   * @return {boolean}
   */
  public static isArchivedAgreement(agr: TenancyAgreementData): boolean {
    return !agr.infiniteTimePeriod && (agr.agreementEndDate ?? Number.MAX_VALUE) < TimeHlp.todayAt(0);
  }

  /**
   * @param {TenancyAgreementData} agr
   * @return {boolean}
   */
  public static isFutureAgreement(agr: TenancyAgreementData): boolean {
    return agr.agreementStartDate > TimeHlp.todayAtEnd();
  }

  /**
   * @param {UnitAccessVM | CommunityTenancyData} ten
   * @return {boolean}
   */
  public static isCurrentCommunityTenancy(ten: UnitAccessVM | CommunityTenancyData): boolean {
    return !ten.cancelDate && ten.startDate < TimeHlp.todayAtEnd() && (ten.endDate ?? Number.MAX_VALUE) > TimeHlp.todayAt(0);
  }

  /**
   * @param {TenancyAgreementData} agr
   * @return {AgreementStatus}
   */
  public static agreementStatus(agr: TenancyAgreementData): AgreementStatus {
    return UserHlp.isCurrentAgreement(agr)
      ? AgreementStatus.ACTIVE
      : UserHlp.isArchivedAgreement(agr)
        ? AgreementStatus.ARCHIVED
        : AgreementStatus.FUTURE;
  }

  /**
   * @param {UserData} user
   * @param {string}   estateId
   * @return {ResidentData}
   */
  public static userDataToPersonalDetails(user: UserData, estateId: string): ResidentData {
    return {
      ...user,
      address: null, // TODO parsing string address
      estateId: estateId,
      userId: user.id,
      email: user.email ? [user.email] : [],
      phone: user.phone ? [user.phone] : [],
      lastUpdate: 0,
      type: LandlordType.PERSON,
      NIP: null,
      REGON: null,
      creatorUserId: null
    };
  }

  public static addContacts(x: UserLike, type: 'email', data: Iterable<string | Nullish>): boolean;
  public static addContacts(x: PersonLike & PhoneLike, type: 'phone', data: Iterable<string | Nullish>): boolean;
  public static addContacts(x: ResidentLike, type: 'email' | 'phone', data: Iterable<string | Nullish>): boolean;

  public static addContacts(person: ResidentLike, type: 'email' | 'phone', data: Iterable<string | Nullish>): boolean {
    const contacts = ArrHlp.make(person[type]);
    const oldSet = new Set(contacts);
    let changed = false;

    // ? Preserving order is important
    for (const contact of data) {
      if (contact && !oldSet.has(contact)) {
        contacts.push(contact);
        changed = true;
      }
    }

    if (changed) person[type] = contacts;

    return changed;
  }
}
